import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { use, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AbilityContext } from '../../ability';
import { useUserPageHref } from '../../components/users/user-page-href';
import { UserSearchCombobox } from '../../components/users/UserSearchCombobox';

export const Route = createFileRoute('/_auth/operations')({
  component: OperatorHome,
});

/**
 * Operator landing page (issue #481).
 *
 * It used to be a pure bulk chooser — two cards, ZIP and CSV — which made the
 * single-person flow invisible from the page an operator lands on: `/users/:oid`
 * was reachable only through the top-bar input or Cmd+K. The search below is the
 * same combobox as the top bar, so one person is a first-class entry point here
 * rather than a shortcut you had to know about.
 *
 * The old `beforeLoad` redirect (skip the page when only one bulk ability is
 * held) is gone with it: the page now carries something no bulk route does, so
 * there is nothing left to skip past.
 */
function OperatorHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ability = use(AbilityContext);
  const canPhotos = ability.can('upload', 'Batch');
  const canCards = ability.can('generate', 'Card');
  const canExport = ability.can('export', 'Batch');
  // `GET /users/search` is `manage:Photo` — same gate as the top-bar search,
  // so a plain user reaching this URL isn't offered a box the API refuses.
  const canSearch = ability.can('manage', 'Photo');
  const [query, setQuery] = useState('');
  const userPageHref = useUserPageHref();

  return (
    <div>
      <header className="section-head">
        <div>
          <p className="label dash">
            {t('operations.subtitle', 'ESPACE OPÉRATEUR')}
          </p>
          <h1 className="display mt-2 text-3xl md:text-5xl">
            {t('operations.title', 'OPÉRATIONS')}
          </h1>
        </div>
        <p className="label">{t('operations.meta', 'UNITÉ · ZIP · CSV')}</p>
      </header>

      {canSearch && (
        <article className="epi-card tone-green mb-6">
          <div className="epi-card-head">
            <span className="num">U</span>
            <span className="title">
              {t('operations.single.title', 'Une personne')}
            </span>
            <span className="meta">
              {t('operations.single.meta', 'RECHERCHE')}
            </span>
          </div>
          <p className="mt-4 mb-3 text-sm text-muted">
            {t(
              'operations.single.description',
              'Ouvrir la page d’une personne pour gérer sa photo ou générer une carte à l’unité.',
            )}
          </p>
          <UserSearchCombobox
            value={query}
            onValueChange={setQuery}
            onSelect={(user) => {
              setQuery('');
              void navigate({ to: '/users/$oid', params: { oid: user.id } });
            }}
            placeholder={t('topSearch.placeholder', 'Chercher un utilisateur…')}
            inputClassName="w-full px-4 py-2.5"
            getItemHref={(user) => userPageHref(user.id)}
            autoFocus
          />
        </article>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {canPhotos && (
          <Link to="/photos/bulk" className="group block">
            <article className="epi-card tone-blue h-full cursor-pointer transition-transform group-hover:-translate-y-1">
              <div className="epi-card-head">
                <span className="num">P</span>
                <span className="title">
                  {t('operations.photos.title', 'Photos en masse')}
                </span>
                <span className="meta">ZIP · UPN</span>
              </div>
              <p className="mt-4 text-sm text-muted">
                {t(
                  'operations.photos.description',
                  'Importer un ZIP de photos nommées par UPN pour upload en lot vers Entra.',
                )}
              </p>
              <p className="mono mt-6 inline-flex items-center gap-2 text-xs text-blue">
                {t('operations.start', 'Commencer')}
                <span aria-hidden>→</span>
              </p>
            </article>
          </Link>
        )}

        {canCards && (
          <Link to="/cards" className="group block">
            <article className="epi-card tone-green h-full cursor-pointer transition-transform group-hover:-translate-y-1">
              <div className="epi-card-head">
                <span className="num">C</span>
                <span className="title">
                  {t('operations.cards.title', 'Cartes en masse')}
                </span>
                <span className="meta">CSV · TEMPLATE</span>
              </div>
              <p className="mt-4 text-sm text-muted">
                {t(
                  'operations.cards.description',
                  'Générer des cartes à partir d’un CSV et d’un template SVG.',
                )}
              </p>
              <p className="mono mt-6 inline-flex items-center gap-2 text-xs text-blue">
                {t('operations.start', 'Commencer')}
                <span aria-hidden>→</span>
              </p>
            </article>
          </Link>
        )}

        {canExport && (
          <Link to="/photos/export" className="group block">
            <article className="epi-card tone-blue h-full cursor-pointer transition-transform group-hover:-translate-y-1">
              <div className="epi-card-head">
                <span className="num">E</span>
                <span className="title">{t('operations.export.title')}</span>
                <span className="meta">ISEE · ZIP</span>
              </div>
              <p className="mt-4 text-sm text-muted">
                {t('operations.export.description')}
              </p>
              <p className="mono mt-6 inline-flex items-center gap-2 text-xs text-blue">
                {t('operations.start', 'Commencer')}
                <span aria-hidden>→</span>
              </p>
            </article>
          </Link>
        )}
      </div>
    </div>
  );
}
